import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { EyeClosedIcon } from "@radix-ui/react-icons";
import { Eye, RefreshCwIcon } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Field, FieldLabel } from "@/components/ui/field";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { cn } from "@/lib/utils";
import { GoogleLoginButton } from "./google-login-button";
import {
  useCreateAccountSchema,
  type CreateAccountSchema,
  type UseCreateAccountReturn,
} from "./use-create-account";

/**
 * Visual layout mode for `CreateAccountForm`.
 *
 * - `"dialog"` (default): preserves the legacy dialog-constrained
 *   geometry — the outermost wrapper centers itself with `mx-auto` and
 *   caps its width at `lg:w-[350px]`, and the submit / OTP-verify
 *   buttons and the terms/privacy links row are shrunk to `w-2/3`.
 *   Used by `LoginRegisterDialog`, where the dialog surface itself
 *   owns the surrounding chrome.
 * - `"onboarding"`: drops the dialog-only constraints so the form
 *   expands to the parent shell width and the buttons/links align to
 *   the available width. Used by `StepSix`, so its width matches the
 *   rest of the onboarding steps instead of looking like a small
 *   centered dialog dropped into the wizard.
 */
export type CreateAccountFormLayout = "dialog" | "onboarding";

/**
 * Optional knobs that compose the registration surface for both the
 * legacy login dialog and the onboarding Step 6 final screen.
 *
 * The dialog embeds the form WITH a Google button and the "switch to
 * login" link. Step 6 embeds the form WITHOUT the Google button (the
 * step places its own Google button above the form) and WITHOUT the
 * "switch to login" link (there is no login surface from onboarding).
 */
export interface CreateAccountFormProps {
  /** State + handlers returned by `useCreateAccount`. */
  hook: UseCreateAccountReturn;
  /** Invoked when the user clicks the "Inicia sesión!" link. */
  onSwitchToLogin?: () => void;
  /** Render the main logo at the top of the form (dialog context). */
  showLogo?: boolean;
  /** Render the Google button inside the form (dialog context). */
  showGoogleButton?: boolean;
  /** Render the "Ya tienes cuenta? Inicia sesión!" link. */
  showSwitchToLogin?: boolean;
  /**
   * Visual layout mode. `"dialog"` (default) preserves the legacy
   * dialog-constrained geometry used by `LoginRegisterDialog`;
   * `"onboarding"` expands the form to the parent shell width and
   * aligns buttons/links to that available width — used by `StepSix`.
   */
  layout?: CreateAccountFormLayout;
  /** Optional className applied to the outermost wrapper. */
  className?: string;
}

/**
 * Dialog-free, reusable email/OTP registration form.
 *
 * Owns no state of its own — delegates every behavior to the
 * `useCreateAccount` hook passed via props. The form is a thin
 * presentation layer over the extracted hook so the same registration
 * flow can be mounted inside the login dialog (today) or the onboarding
 * Step 6 final screen (new) without duplicating sensitive auth logic.
 */
export const CreateAccountForm = ({
  hook,
  onSwitchToLogin,
  showLogo = false,
  showGoogleButton = false,
  showSwitchToLogin = false,
  layout = "dialog",
  className,
}: CreateAccountFormProps) => {
  const form = useForm<CreateAccountSchema>({
    resolver: zodResolver(useCreateAccountSchema),
    defaultValues: {
      username: "",
      firstName: "",
      password: "",
    },
  });

  // Reset the form when the hook transitions into the OTP screen so the
  // password field is cleared once the backend accepted the registration.
  useEffect(() => {
    if (hook.confirmMessage) {
      form.reset();
    }
  }, [hook.confirmMessage, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await hook.onSubmit(values);
  });

  // Onboarding layout drops the dialog-only width constraints so the
  // form expands to the parent shell width (no `mx-auto` centering and
  // no `lg:w-[350px]` cap). The submit / verify buttons and the
  // terms/privacy links row also drop their `w-2/3` shrink and align
  // to the available width.
  const isOnboardingLayout = layout === "onboarding";
  const wrapperClassName = cn(
    "grid w-full gap-3 pt-2 pb-6",
    isOnboardingLayout ? null : "mx-auto lg:w-[350px]",
    className,
  );
  const submitRowChildWidthClass = isOnboardingLayout ? "w-full" : "w-2/3";

  return (
    <div className={wrapperClassName}>
      {showLogo && (
        <div className="flex justify-center pt-2 pb-4">
          <span className="text-xl font-bold">Carriapp</span>
        </div>
      )}

      {showGoogleButton && (
        <div
          data-testid="create-account-google-area"
          data-state={hook.confirmMessage ? "collapsed" : "expanded"}
          aria-hidden={hook.confirmMessage ? "true" : undefined}
          className={cn(
            "transition-all duration-300 ease-in-out overflow-hidden",
            hook.confirmMessage
              ? "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
              : "max-h-32 opacity-100 translate-y-0",
          )}
        >
          <div className="flex flex-col gap-6"> 
            <GoogleLoginButton text="Regístrate con Google" />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground font-bold">
                  O regístrate con tu email
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline divider + label used by Step 6 (where the Google button
          is rendered by the parent above the form). */}
      {!showGoogleButton && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t"></span>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground font-bold">
              registrate con tu email
            </span>
          </div>
        </div>
      )}

      {hook.confirmMessage ? (
        <div className="flex flex-col w-full">
          <div className="flex justify-center">
            <Field className="mx-auto">
              <div className="flex items-center justify-between">
                <FieldLabel htmlFor="otp-verification">
                  Código de activación
                </FieldLabel>
                <Button
                  variant="outline"
                  size="xs"
                  type="button"
                  onClick={hook.onResendCode}
                  disabled={
                    hook.isResendLoading ||
                    hook.resendCooldown > 0 ||
                    !hook.activationEmail
                  }
                >
                  <RefreshCwIcon />
                  {hook.isResendLoading
                    ? "Reenviando..."
                    : hook.resendCooldown > 0
                      ? `Re-enviar en ${hook.resendCooldown}s`
                      : "Re-enviar código"}
                </Button>
              </div>
              <InputOTP
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={hook.otpCode}
                onChange={(value) => {
                  hook.setOtpCode(value);
                  if (hook.errorMessage !== "") {
                    hook.setOtpCode(value);
                  }
                  if (hook.otpIsInvalid && value.length === 6) {
                    hook.setOtpCode(value);
                  }
                }}
                aria-invalid={hook.otpIsInvalid}
                className="justify-center"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              {hook.otpIsInvalid && (
                <p className="text-destructive text-xs mt-2 text-center">
                  Ingresa un código válido de 6 dígitos.
                </p>
              )}
            </Field>
          </div>
          <button
            type="button"
            onClick={hook.onBackToRegister}
            className="flex justify-center items-center mt-4 underline text-muted-foreground"
          >
            No es mi correo electronico
          </button>
        </div>
      ) : null}

      {hook.errorMessage !== "" && (
        <p className="text-destructive text-sm text-center">{hook.errorMessage}</p>
      )}

      {!hook.confirmMessage ? (
        <div className="flex flex-col gap-3 pt-2">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        aria-label="Nombre"
                        placeholder="Nombre"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        aria-label="Email"
                        type="email"
                        placeholder="Email"
                        autoComplete="email"
                        required
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Esto se usará como tu nombre de usuario.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <InputGroup>
                        <InputGroupInput
                          aria-label="Contraseña"
                          type={hook.pwdVisible ? "text" : "password"}
                          placeholder="Contraseña"
                          {...field}
                          autoComplete="new-password"
                        />
                        <InputGroupAddon align="inline-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            type="button"
                            className="-ml-8"
                            tabIndex={-1}
                            onClick={(event) => {
                              event.preventDefault();
                              hook.setPwdVisible(!hook.pwdVisible);
                            }}
                          >
                            {hook.pwdVisible ? (
                              <EyeClosedIcon className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <Eye className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                        </InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col items-center justify-center w-full gap-2">
                <div
                  className={cn(
                    "flex flex-row justify-between text-xxs opacity-50 px-2 pt-4",
                    submitRowChildWidthClass,
                  )}
                >
                  <Link
                    to="/terms-of-service"
                    className="text-muted-foreground"
                  >
                    Términos del Servicio
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className="text-muted-foreground"
                  >
                    Política de Privacidad
                  </Link>
                </div>
                <Button
                  type="submit"
                  disabled={hook.isLoading || !hook.reCaptchaIsReady}
                  size="lg"
                  className={submitRowChildWidthClass}
                >
                  {hook.isLoading ? "Creando..." : "Crea tu cuenta"}
                </Button>
              </div>
            </form>
          </Form>
          {showSwitchToLogin && (
            <div className="mt-4 text-center text-sm">
              <span className="mr-2 text-muted-foreground">
                Ya tienes cuenta?
              </span>
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="inline-block text-sm underline cursor-pointer"
              >
                ¡Inicia sesión!
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center w-full gap-2 mt-4">
          <Button
            type="button"
            disabled={hook.isLoading || !hook.reCaptchaIsReady}
            onClick={hook.onVerifyAccount}
            size="lg"
            className={submitRowChildWidthClass}
          >
            {hook.isLoading ? "Verificando..." : "Verificar cuenta"}
          </Button>
        </div>
      )}
    </div>
  );
};
